/**
 * ReportGenerator — Report type selection + generation + display.
 */

import { useState } from 'react';
import { useAuth } from '../../../components/AuthProvider';
import { useToast } from '../../../components/ToastProvider';
import { generateReport } from '../../../api/reports';
import type { ReportRequest, ReportResponse } from '../../../api/types';

const REPORT_TYPES = [
  { id: 'executive_summary', label: 'Executive Summary', description: 'High-level threat overview for leadership' },
  { id: 'technical_brief', label: 'Technical Brief', description: 'Detailed indicators of compromise for SOC teams' },
  { id: 'mitre_report', label: 'MITRE Report', description: 'ATT&CK technique coverage analysis' },
  { id: 'mitigation_plan', label: 'Mitigation Plan', description: 'Comprehensive mitigation recommendations' },
] as const;

export function ReportGenerator() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [selectedType, setSelectedType] = useState<string>('executive_summary');
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!user) return;
    setGenerating(true);
    setReport(null);
    setGenError(null);

    try {
      // Get most recent completed document
      const { listDocuments } = await import('../../../api/documents');
      const docsResp = await listDocuments(user.tenantId);
      const completedDocs = docsResp.documents.filter(d => d.status === 'completed');
      const latestDoc = completedDocs.length > 0 ? completedDocs[completedDocs.length - 1] : null;

      if (!latestDoc) {
        setGenError('No completed documents found. Upload and process a CTI document first.');
        setGenerating(false);
        return;
      }

      const response = await generateReport({
        tenant_id: user.tenantId,
        report_type: selectedType as ReportRequest['report_type'],
        document_ids: [latestDoc.document_id],
      });
      setReport(response);
      showToast('Report generated successfully', 'success');
    } catch {
      setGenError('Failed to generate report. The backend may be unavailable or no documents have been processed yet.');
      showToast('Failed to generate report', 'error');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">Report Generator</h1>
        <p className="text-body text-slate-muted mt-1">
          Generate CTI reports from analyzed intelligence
        </p>
      </div>

      {/* Report type selection */}
      <div className="glass-card">
        <h3 className="text-section-sm font-bold text-slate-text mb-4">Select Report Type</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {REPORT_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`text-left p-4 rounded-card border-2 transition-all ${
                selectedType === type.id
                  ? 'border-primary-teal bg-primary-teal/5'
                  : 'border-gray-200 hover:border-primary-blue/50'
              }`}
            >
              <p className="text-body font-bold text-slate-text">{type.label}</p>
              <p className="text-[16px] text-slate-muted mt-1">{type.description}</p>
            </button>
          ))}
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating}
          className="btn-primary mt-6 w-full sm:w-auto"
        >
          {generating ? 'Generating...' : 'Generate Report'}
        </button>
      </div>

      {/* Generation error */}
      {genError && !report && (
        <div className="glass-card text-center py-8">
          <p className="text-body text-red-700">{genError}</p>
        </div>
      )}

      {/* Generated report */}
      {report && (
        <div className="glass-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-section-sm font-bold text-slate-text">Generated Report</h3>
            <span className="text-[14px] text-slate-muted">
              {new Date(report.generated_at).toLocaleString()}
            </span>
          </div>
          <div className="prose prose-lg max-w-none text-slate-text whitespace-pre-wrap">
            {report.content}
          </div>
          <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-table-border)' }}>
            <p className="text-[14px] text-slate-muted">
              Report ID: <code className="font-mono">{report.report_id}</code> | Type: {report.report_type}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
